import { getAuth } from 'firebase/auth';
import { io, Socket } from 'socket.io-client';
import { resolveHybridSocketBaseUrl } from './hybridFlags';

/**
 * חיבור Socket.IO יחיד ומשותף לכל השירותים בזמן-אמת (הודעות + שינויי קריאות/נעילות).
 * בעבר MessagingService ו-AppRealtimeService פתחו כל אחד חיבור נפרד לאותו שרת —
 * ה-Hub מאחד אותם לחיבור אחד, וכל שירות רושם את ה-listeners שלו דרך onSocket().
 */
type SocketConsumer = (socket: Socket) => void;

class RealtimeSocketHub {
  private socket: Socket | null = null;
  private userId: string | null = null;
  private connectingPromise: Promise<Socket | null> | null = null;
  /** נקראים בכל פעם שנוצר socket חדש (כולל חיבור ראשון) — שם כל שירות מצמיד listeners. */
  private socketConsumers: Set<SocketConsumer> = new Set();
  /** נקראים בכל אירוע connect (כולל reconnect) — לרענון נתונים אחרי חזרה לחיבור. */
  private connectListeners: Set<() => void> = new Set();
  /** נקראים כשהטאב/המכשיר "מתעורר" (חוזר לפוקוס / חוזר לאונליין) — רשת חוזר לאחר שינה/רקע ממושך. */
  private wakeListeners: Set<() => void> = new Set();
  private wakeListenersRegistered = false;

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return Boolean(this.socket?.connected);
  }

  async ensureConnected(userId: string): Promise<Socket | null> {
    const uid = String(userId || '').trim();
    if (!uid) return null;
    this.registerWakeListeners();
    if (this.socket && this.userId === uid) {
      // ה-socket קיים אבל ה-manager עצר ניסיונות reconnect (חיבור מת בשקט) — נאתחל ניסיון מיידי
      // במקום לחכות לאירוע wake הבא (online/visibility). לא חוסם — .connect() אידמפוטנטי.
      if (!this.socket.connected) this.socket.connect();
      return this.socket;
    }
    if (this.connectingPromise) return this.connectingPromise;

    this.connectingPromise = (async () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser || String(currentUser.uid || '').trim() !== uid) return null;
        const socketBaseUrl = resolveHybridSocketBaseUrl();
        if (!socketBaseUrl) return null;
        const token = await currentUser.getIdToken();
        this.teardownSocket();
        const socket = io(socketBaseUrl, {
          transports: ['websocket', 'polling'],
          auth: { token },
          timeout: 8000,
          reconnection: true,
          // ללא הגבלת ניסיונות — Wi-Fi/סלולר שנופל לכמה שניות (או מכשיר שיוצא משינה) לא אמור
          // להשאיר את החיבור מת לצמיתות עד רענון ידני של הדף. ה-backoff (reconnectionDelay
          // עד reconnectionDelayMax) כבר מונע הצפה של ניסיונות.
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        });
        this.socket = socket;
        this.userId = uid;
        socket.on('connect', () => {
          this.connectListeners.forEach((cb) => {
            try {
              cb();
            } catch {
              /* noop */
            }
          });
        });
        // רענון טוקן אוטומטי על שגיאת חיבור — ה-reconnection ינסה שוב עם הטוקן המעודכן.
        socket.on('connect_error', async () => {
          try {
            const freshUser = getAuth().currentUser;
            if (!freshUser || !this.socket) return;
            const freshToken = await freshUser.getIdToken(true);
            this.socket.auth = { token: freshToken };
          } catch {
            /* ignore transient auth refresh errors */
          }
        });
        // כל הצרכנים מצמידים את ה-listeners שלהם ל-socket החדש.
        this.socketConsumers.forEach((consumer) => {
          try {
            consumer(socket);
          } catch {
            /* noop */
          }
        });
        return socket;
      } catch {
        return null;
      } finally {
        this.connectingPromise = null;
      }
    })();

    return this.connectingPromise;
  }

  /**
   * רישום צרכן שמצמיד listeners ל-socket. נקרא מיד אם כבר קיים socket,
   * ושוב בכל יצירת socket חדש (reconnect אחרי ניתוק מלא).
   * @returns פונקציית ביטול רישום.
   */
  onSocket(consumer: SocketConsumer): () => void {
    this.socketConsumers.add(consumer);
    if (this.socket) {
      try {
        consumer(this.socket);
      } catch {
        /* noop */
      }
    }
    return () => {
      this.socketConsumers.delete(consumer);
    };
  }

  onConnect(callback: () => void): () => void {
    this.connectListeners.add(callback);
    return () => {
      this.connectListeners.delete(callback);
    };
  }

  /**
   * נקרא כשהמכשיר "מתעורר": הטאב חוזר לפוקוס/גלוי, או שהרשת חוזרת לאונליין.
   * זה בדיוק הרגע שבו socket.io נוטה להישאר מנותק בשקט (מחשב נכנס לשינה / טלפון ננעל / דפדפן
   * מקפיא טאבים ברקע) — לכן מכריחים ניסיון חיבור מיידי, וגם מפעילים רענון נתונים בכל מקרה
   * (גם אם ה-socket בעצם עדיין מחובר) כדי לתפוס הודעות שאולי לא הגיעו בזמן שהמכשיר היה "ישן".
   */
  onWake(callback: () => void): () => void {
    this.wakeListeners.add(callback);
    return () => {
      this.wakeListeners.delete(callback);
    };
  }

  private registerWakeListeners(): void {
    if (this.wakeListenersRegistered || typeof window === 'undefined') return;
    this.wakeListenersRegistered = true;
    const onWakeEvent = () => {
      if (this.socket && !this.socket.connected) this.socket.connect();
      this.wakeListeners.forEach((cb) => {
        try {
          cb();
        } catch {
          /* noop */
        }
      });
    };
    window.addEventListener('online', onWakeEvent);
    window.addEventListener('focus', onWakeEvent);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') onWakeEvent();
      });
    }
  }

  private teardownSocket(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
    this.socket = null;
  }

  disconnect(): void {
    this.teardownSocket();
    this.userId = null;
    this.connectingPromise = null;
  }
}

export const realtimeSocketHub = new RealtimeSocketHub();
